import React, { useState } from 'react';
import { useAuth } from '../lib/auth-context';
import api from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Mail, Phone, Shield, Wallet, Lock as LockIcon, Key, Eye, EyeOff, Store, MapPin, Loader2, UserCircle, Edit, Save, X } from 'lucide-react';
import { format } from 'date-fns';

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changing, setChanging] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);

  const startEdit = () => {
    setEditData({
      name: user?.name || '',
      phone: user?.phone || '',
      shop_name: user?.shop_name || '',
      address: user?.address || '',
      postal_code: user?.postal_code || '',
      city: user?.city || '',
      state: user?.state || '',
    });
    setEditing(true);
  };

  const handlePincodeLookup = async (pincode) => {
    if (!pincode || pincode.length !== 6) return;
    setPinLoading(true);
    try {
      const r = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await r.json();
      if (data?.[0]?.Status === 'Success' && data[0].PostOffice?.length) {
        const po = data[0].PostOffice[0];
        setEditData(prev => ({ ...prev, city: po.District || '', state: po.State || '' }));
        toast.success(`${po.District}, ${po.State}`);
      }
    } catch {}
    setPinLoading(false);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, editData);
      toast.success('Profile updated');
      setEditing(false);
      if (refreshUser) refreshUser();
      else window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) { toast.error('Fill all fields'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 6) { toast.error('Minimum 6 characters'); return; }
    setChanging(true);
    try {
      await api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
      toast.success('Password changed successfully');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    }
    setChanging(false);
  };

  const usable = (user?.wallet_balance || 0) - (user?.lock_balance || 0);

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-4" data-testid="profile-page">
        {/* Profile Header Card */}
        <Card className="border-0 shadow-md overflow-hidden rounded-2xl">
          <div className="h-24 sm:h-28 bg-gradient-to-r from-[#4680ff] to-[#2563eb] relative">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50" />
          </div>
          <div className="px-4 sm:px-6 pb-5 -mt-10 sm:-mt-12 relative">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-white border-4 border-white shadow-lg flex items-center justify-center flex-shrink-0">
                <span className="text-3xl sm:text-4xl font-bold text-[#4680ff]">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0 pb-1">
                <h2 className="text-lg sm:text-xl font-bold text-slate-900 truncate">{user?.name}</h2>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <Badge className="bg-[#4680ff]/10 text-[#4680ff] border-[#4680ff]/20 text-[11px] capitalize">{user?.role?.replace('_', ' ')}</Badge>
                  <Badge className={`text-[11px] ${user?.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                    {user?.status === 'active' ? 'Active' : 'Frozen'}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Wallet Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Card className="border border-emerald-100 shadow-sm rounded-xl">
            <CardContent className="p-3 sm:p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <Wallet className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] sm:text-[10px] text-slate-400 font-semibold uppercase">Balance</p>
                <p className="text-base sm:text-lg font-bold text-slate-900 font-mono truncate" data-testid="profile-balance">
                  &#8377;{(user?.wallet_balance || 0).toLocaleString('en-IN')}
                </p>
              </div>
            </CardContent>
          </Card>
          {(user?.lock_balance || 0) > 0 && (
            <Card className="border border-red-100 shadow-sm rounded-xl">
              <CardContent className="p-3 sm:p-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                  <LockIcon className="w-4 h-4 text-red-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] sm:text-[10px] text-slate-400 font-semibold uppercase">Locked</p>
                  <p className="text-base sm:text-lg font-bold text-red-600 font-mono truncate">&#8377;{(user?.lock_balance || 0).toLocaleString('en-IN')}</p>
                </div>
              </CardContent>
            </Card>
          )}
          <Card className="border border-blue-100 shadow-sm rounded-xl">
            <CardContent className="p-3 sm:p-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                <Wallet className="w-4 h-4 text-blue-600" />
              </div>
              <div className="min-w-0">
                <p className="text-[9px] sm:text-[10px] text-slate-400 font-semibold uppercase">Usable</p>
                <p className="text-base sm:text-lg font-bold text-blue-700 font-mono truncate">&#8377;{usable.toLocaleString('en-IN')}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Account Details */}
        <Card className="border border-slate-100 shadow-sm rounded-2xl overflow-hidden">
          <div className="px-4 sm:px-5 py-3 bg-slate-50/50 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-700">Account Details</h3>
            {!editing ? (
              <Button variant="outline" size="sm" className="h-7 px-3 text-xs rounded-lg" onClick={startEdit} data-testid="edit-profile-btn">
                <Edit className="w-3 h-3 mr-1.5" /> Edit
              </Button>
            ) : (
              <div className="flex gap-1.5">
                <Button variant="outline" size="sm" className="h-7 px-2 text-xs rounded-lg" onClick={() => setEditing(false)}>
                  <X className="w-3 h-3" />
                </Button>
                <Button size="sm" className="h-7 px-3 text-xs rounded-lg bg-[#4680ff] hover:bg-[#2563eb] text-white" onClick={handleSaveProfile} disabled={saving} data-testid="save-profile-btn">
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" /> Save</>}
                </Button>
              </div>
            )}
          </div>
          <CardContent className="p-4 sm:p-5">
            {!editing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InfoRow icon={UserCircle} label="Username" value={user?.username ? `@${user.username}` : '-'} mono testId="profile-username" />
                <InfoRow icon={Mail} label="Email" value={user?.email} testId="profile-email" />
                <InfoRow icon={Phone} label="Phone" value={user?.phone} mono testId="profile-phone" />
                <InfoRow icon={Store} label="Shop Name" value={user?.shop_name || '-'} testId="profile-shop" />
                <InfoRow icon={MapPin} label="Address" value={user?.address || '-'} testId="profile-address" />
                <InfoRow icon={MapPin} label="Postal Code" value={user?.postal_code || '-'} mono testId="profile-pincode" />
                <InfoRow icon={MapPin} label="City" value={user?.city || '-'} testId="profile-city" />
                <InfoRow icon={MapPin} label="State" value={user?.state || '-'} testId="profile-state" />
                <InfoRow icon={Shield} label="Role" value={user?.role?.replace('_', ' ')} upper testId="profile-role" />
                <InfoRow icon={Shield} label="Member Since" value={user?.created_at ? format(new Date(user.created_at), 'dd MMM yyyy') : '-'} testId="profile-since" />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-semibold text-slate-400 uppercase">Name</Label>
                    <Input value={editData.name} onChange={e => setEditData({ ...editData, name: e.target.value })} className="h-9 rounded-lg text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-semibold text-slate-400 uppercase">Phone</Label>
                    <Input value={editData.phone} onChange={e => setEditData({ ...editData, phone: e.target.value })} className="h-9 rounded-lg text-sm font-mono" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold text-slate-400 uppercase">Shop Name</Label>
                  <Input value={editData.shop_name} onChange={e => setEditData({ ...editData, shop_name: e.target.value })} className="h-9 rounded-lg text-sm" placeholder="Enter shop name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-semibold text-slate-400 uppercase">Address</Label>
                  <Input value={editData.address} onChange={e => setEditData({ ...editData, address: e.target.value })} className="h-9 rounded-lg text-sm" placeholder="Enter address" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-semibold text-slate-400 uppercase">Postal Code {pinLoading && <Loader2 className="w-3 h-3 inline animate-spin" />}</Label>
                    <Input value={editData.postal_code} onChange={e => {
                      const v = e.target.value.replace(/\D/g, '').substring(0, 6);
                      setEditData({ ...editData, postal_code: v });
                      if (v.length === 6) handlePincodeLookup(v);
                    }} className="h-9 rounded-lg text-sm font-mono" placeholder="6 digit" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-semibold text-slate-400 uppercase">City</Label>
                    <Input value={editData.city} onChange={e => setEditData({ ...editData, city: e.target.value })} className="h-9 rounded-lg text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-semibold text-slate-400 uppercase">State</Label>
                    <Input value={editData.state} onChange={e => setEditData({ ...editData, state: e.target.value })} className="h-9 rounded-lg text-sm" />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card className="border border-slate-100 shadow-sm rounded-2xl overflow-hidden">
          <div className="px-4 sm:px-5 py-3 bg-slate-50/50 border-b border-slate-100">
            <h3 className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <Key className="w-4 h-4 text-slate-400" /> Change Password
            </h3>
          </div>
          <CardContent className="p-4 sm:p-5 space-y-3">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold text-slate-400 uppercase">Current Password</Label>
              <div className="relative">
                <Input type={showCurrent ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="h-10 rounded-lg pr-10 text-sm" placeholder="Enter current password" data-testid="current-password" />
                <button onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold text-slate-400 uppercase">New Password</Label>
                <div className="relative">
                  <Input type={showNew ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} className="h-10 rounded-lg pr-10 text-sm" placeholder="Min 6 characters" data-testid="new-password" />
                  <button onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-semibold text-slate-400 uppercase">Confirm Password</Label>
                <Input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="h-10 rounded-lg text-sm" placeholder="Repeat new password" data-testid="confirm-password" />
              </div>
            </div>
            <Button onClick={handleChangePassword} disabled={changing} className="w-full sm:w-auto bg-[#4680ff] hover:bg-[#2563eb] text-white rounded-lg h-10 px-6 text-sm font-semibold mt-1" data-testid="change-password-btn">
              {changing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {changing ? 'Changing...' : 'Change Password'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

const InfoRow = ({ icon: Icon, label, value, mono, upper, testId }) => (
  <div className="flex items-center gap-3 p-3 bg-slate-50/50 rounded-xl border border-slate-100/80" data-testid={testId}>
    <div className="w-8 h-8 rounded-lg bg-white border border-slate-100 flex items-center justify-center flex-shrink-0 shadow-sm">
      <Icon className="w-3.5 h-3.5 text-slate-400" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className={`text-sm text-slate-800 font-medium truncate ${mono ? 'font-mono' : ''} ${upper ? 'capitalize' : ''}`}>{value || '-'}</p>
    </div>
  </div>
);

export default ProfilePage;
